import { lazy, Suspense, useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	isRedirect,
	Outlet,
	redirect,
	retainSearchParams,
} from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { isDemoPresentation } from '@/lib/constants'
import { clearQueryCache } from '@/lib/react-query'

import { AppHeader, HeaderSkeleton } from '@/features/app-shell/app-header'
import { AppLoader } from '@/features/app-shell/app-loader'
import { useAppLoader } from '@/features/app-shell/app-loader-context'
import { appLoaderStore } from '@/features/app-shell/app-loader-store'
import { NotFoundContent } from '@/features/app-shell/not-found-content'
import {
	AppSidebar,
	AppSidebarSkeleton,
} from '@/features/app-shell/sidebar/app-sidebar'
import { DemoCtaBottomBar } from '@/features/demo/components/demo-cta-bottom-bar'
import { DemoHidden } from '@/features/demo/components/demo-hidden'
import { isDemoExcludedPath } from '@/features/demo/excluded-routes'
import { useActiveTourId } from '@/features/guided-tour/store'
import { NoIntegrationsAlert } from '@/features/integrations/components/no-integrations-alert'
import { integrationQueries } from '@/features/integrations/queries'
import { OnboardingStoreProvider } from '@/features/onboarding/provider'
import { findMissingIntegrationGate } from '@/features/tenant/post-switch-path'
import { useErrorBoundary } from '@/hooks/use-error-boundary'

import { useAppActions, useAppSidebar } from '@/shared/app/store'
import { useMe } from '@/shared/auth/queries'
import { getCurrentTenant, useCurrentTenant } from '@/shared/auth/store'
import { tenantSessionQueries } from '@/shared/auth/tenant-session'
import { seedDashboardSession } from '@/shared/auth/utils/router'
import { GlobalPanel } from '@/shared/panel/components/panel'

import {
	DefaultErrorFallback,
	ErrorBoundary,
} from '@/components/common/error-boundary'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { Toaster as Sonner } from '@/components/ui/sonner'

import type { ErrorComponentProps } from '@tanstack/react-router'

// Lazy load non-critical components
const NavigationLoader = lazy(
	() => import('@/features/app-shell/navigation-loader'),
)
const ConfirmationDialog = lazy(
	() => import('@/features/confirmation-dialog/components/confirmation-dialog'),
)
const BetaAnnouncementDialog = lazy(
	() =>
		import('@/shared/beta-announcement/components/beta-announcement-dialog'),
)
const Wizard = lazy(
	() => import('@/features/integrations/wizard/components/wizard'),
)
const GlobalBannerList = lazy(
	() => import('@/features/notifications/components/global-banner-list'),
)
const GuidedTourHost = lazy(
	() => import('@/features/guided-tour/components/guided-tour-host'),
)
// Renders nothing outside the demo build and pulls in no tour content — the
// 16-stop definition rides its own folded dynamic import (`demo/tour/loader`).
const DemoTourAutoStart = lazy(
	() => import('@/features/demo/tour/components/demo-tour-auto-start'),
)

/**
 * Dashboard URL search schema.
 *
 * `t` is the AIR tenant id carried in the URL. Named `t` (not `tenant`)
 * deliberately: Microsoft's OAuth admin-consent popup redirects with its own
 * `?tenant=<UUID>` we don't control, and if our dashboard layout read that
 * key the parent `beforeLoad` would mis-interpret Microsoft's tenant UUID as
 * an AIR tenant switch. Keeping our param name distinct sidesteps the
 * collision entirely.
 */
const dashboardSearchSchema = z.object({
	t: z.string().optional(),
})

/**
 * Dashboard layout that wraps the entire dashboard.
 */
export const Route = createFileRoute('/_dashboard')({
	validateSearch: zodValidator(dashboardSearchSchema),

	search: {
		middlewares: [retainSearchParams(['t'])],
	},

	// Without this, TanStack Router does NOT re-run the parent beforeLoad when
	// only search.t changes (parent stays matched across child navigation).
	// `loaderDeps` declares the dependency so beforeLoad re-runs on every
	// tenant change — the keystone of the router-driven switch flow below.
	loaderDeps: ({ search }) => ({ t: search.t }),

	/**
	 * Single orchestrator for tenant switches.
	 *
	 * Every URL change to `?t=X` (dropdown, cross-tenant Link, pasted URL,
	 * smart-redirect, cold-load with stale store) routes through here. We:
	 *   1. Detect a tenant switch by comparing URL tenant to current store.
	 *   2. Show the AppLoader before any tenant-mismatched render can occur.
	 *   3. Cancel in-flight queries + invalidate cache for the old tenant.
	 *   4. Hand off to `seedDashboardSession` to validate, set the store, and
	 *      prefetch the new tenant's data.
	 *   5. Run the sticky-URL redirect so the URL always carries `?t=`.
	 *
	 * The loader is hidden by `DashboardLayout`'s data-readiness effect once
	 * the new tree renders with fresh data (not from here, so we don't hide
	 * before child loaders run).
	 *
	 * @see knowledge/reference/tenant-switching.md
	 */
	beforeLoad: async ({ context: { clerk, queryClient }, location, search }) => {
		// Demo containment (ADR 0006 dec. 5 · issue 03): demo-excluded surfaces
		// redirect home instead of rendering — blocks deep-links / pasted URLs with
		// no error or blank screen. Short-circuits before tenant seeding since the
		// target route is never shown. Non-demo builds skip this entirely. Route set
		// is the single source of truth in `features/demo/excluded-routes`.
		if (isDemoPresentation && isDemoExcludedPath(location.pathname)) {
			throw redirect({ to: '/home', search, replace: true })
		}

		const prevTenant = getCurrentTenant()
		const isTenantSwitch = !!prevTenant && !!search.t && search.t !== prevTenant

		const { showLoader, hideLoader, setShowProgress } =
			appLoaderStore.getState().actions

		if (isTenantSwitch) {
			showLoader()
			setShowProgress(true)
			await clearQueryCache()
		}

		try {
			await seedDashboardSession({
				clerk,
				queryClient,
				location,
				search,
				// The route names the feature, not `shared/auth` (A-01/A-02): the
				// integration list is warmed here because the gate check below reads
				// it straight back out of cache. Started inside `seedDashboardSession`'s
				// own `Promise.all`, so it still runs parallel to the tenant details.
				prefetch: (tenant) => [
					queryClient.ensureQueryData({
						...integrationQueries.list(tenant),
						revalidateIfStale: true,
					}),
				],
			})

			// Keep ?t= sticky in the URL so case/share links always carry the active tenant.
			const currentTenant = getCurrentTenant()
			if (currentTenant && search.t !== currentTenant) {
				throw redirect({
					to: location.pathname,
					search: { ...search, t: currentTenant },
					replace: true,
				})
			}

			// Integration gate: catches cross-tenant link clicks / pasted URLs
			// that land on an integration-gated route in a tenant without that
			// integration installed. (Dropdown switches are already gated
			// upstream by useSwitchTenant.) Uses the same helper as
			// `resolvePostSwitchPath` so the two checks can never drift.
			//
			// `getQueryData` returns `undefined` on cache miss (prefetch
			// failure, network hiccup); the helper interprets that as "skip
			// the gate" rather than false-redirect.
			if (currentTenant) {
				const integrations = queryClient.getQueryData(
					integrationQueries.list(currentTenant).queryKey,
				)
				if (findMissingIntegrationGate(location.pathname, integrations)) {
					throw redirect({
						to: '/home',
						search: { t: currentTenant },
						replace: true,
					})
				}
			}
		} catch (error) {
			// Drop the loader on real errors so the error component can render.
			// `redirect` throws are routed back through the router and re-enter
			// beforeLoad with the corrected URL — we want the loader to stay up
			// across the redirect, so only hide on non-redirect errors.
			if (isTenantSwitch && !isRedirect(error)) hideLoader()
			throw error
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Dashboard | BitLyft AIR®',
			},
		],
	}),

	component: DashboardLayout,
	pendingComponent: DashboardSkeleton,
	errorComponent: DashboardError,
	notFoundComponent: NotFoundContent,
})

function DashboardLayout() {
	const {
		data: { user },
	} = useMe()

	const currentTenant = useCurrentTenant()
	const { data: tenantDetails, isFetching: isFetchingTenantDetails } = useQuery(
		{
			...tenantSessionQueries.details(currentTenant),
		},
	)

	const isSidebarOpen = useAppSidebar()
	const { toggleSidebar } = useAppActions()
	const { hideLoader } = useAppLoader()
	const activeTourId = useActiveTourId()

	// Gate on `!isFetching` so a stale-but-cached tenantDetails (e.g. switching
	// back to a previously visited tenant) doesn't hide the loader prematurely
	// while the background refetch is still in flight.
	useEffect(() => {
		if (!!user && !!tenantDetails && !isFetchingTenantDetails) {
			hideLoader()
		}
	}, [hideLoader, user, tenantDetails, isFetchingTenantDetails])

	return (
		<SidebarProvider
			open={isSidebarOpen}
			onOpenChange={toggleSidebar}
			defaultOpen={isSidebarOpen}
			data-testid="app-shell"
			// The shell is exactly one viewport tall and never scrolls itself; the
			// inner `<main>` below is the only scroller. `SidebarProvider`'s own
			// `min-h-svh` lets the wrapper grow with content, which would push the
			// bottom CTA bar (ADR 0015 dec. 6) off-screen on any long route.
			className="h-svh overflow-hidden"
		>
			<OnboardingStoreProvider>
				<AppSidebar />

				<SidebarInset className="min-h-0">
					<AppHeader />

					{/*
					 * `min-h-0 flex-1` is what makes this the ONLY scroller: a flex child's
					 * default `min-height:auto` refuses to shrink below its content, so
					 * without it the column overflows and the shell scrolls instead.
					 * Replaces a `max-h-[calc(100svh-(--spacing(24)))]` budget that
					 * hardcoded the header height — the bottom CTA bar added a second
					 * chrome row the calc couldn't account for.
					 */}
					<main className="mx-auto flex min-h-0 w-full flex-1 flex-col items-center justify-start overflow-y-auto">
						<ErrorBoundary feature="global-banner-list" fallback={() => <></>}>
							<Suspense fallback={null}>
								<GlobalBannerList />
							</Suspense>
						</ErrorBoundary>

						<NoIntegrationsAlert />

						<Outlet />

						<ErrorBoundary feature="global-panel" fallback={() => <></>}>
							<Suspense fallback={null}>
								<GlobalPanel />
							</Suspense>
						</ErrorBoundary>
					</main>

					<Suspense fallback={null}>
						<Sonner />
						{/* Onboarding is Graylog-integration setup — a write flow the demo
						    hides. Not mounted rather than rendered-empty: the
						    wizard opens itself off onboarding progress. */}
						<DemoHidden>
							<Wizard />
						</DemoHidden>
						<NavigationLoader />
						<ConfirmationDialog />
						<BetaAnnouncementDialog />
						<DemoTourAutoStart />
						{activeTourId && <GuidedTourHost />}
					</Suspense>

					<DemoCtaBottomBar />
				</SidebarInset>
			</OnboardingStoreProvider>
		</SidebarProvider>
	)
}

/**
 * Error component for the dashboard.
 * Renders WITHOUT AppSidebar/AppHeader to avoid useCurrentTenant() assert
 * when the store is not initialized (e.g. /me failure).
 */
function DashboardError({ error, reset: routeReset }: ErrorComponentProps) {
	const { reset } = useErrorBoundary()

	const handleReset = () => {
		reset()
		routeReset()
	}

	return (
		<div className="flex min-h-svh w-full flex-col items-center justify-center">
			<DefaultErrorFallback resetError={handleReset} error={error} />
			<Suspense fallback={null}>
				<Sonner />
			</Suspense>
		</div>
	)
}

function DashboardSkeleton() {
	const isSidebarOpen = useAppSidebar()

	return (
		<SidebarProvider
			open={isSidebarOpen}
			onOpenChange={() => {}}
			defaultOpen={isSidebarOpen}
			// Same one-viewport shell as `DashboardLayout` — the two must size
			// identically or the loader visibly jumps when the real layout swaps in.
			className="h-svh overflow-hidden"
		>
			{/* No OnboardingStoreProvider here — it requires tenant data from beforeLoad */}
			<AppSidebarSkeleton />

			<SidebarInset className="min-h-0">
				<HeaderSkeleton />

				<main className="mx-auto flex min-h-0 w-full flex-1 flex-col items-center justify-start overflow-y-auto">
					<AppLoader isVisible showProgress />
				</main>
			</SidebarInset>
		</SidebarProvider>
	)
}
